import React, { useContext, useEffect, useState } from "react";
import { Box, Chip, CircularProgress, Typography } from "@mui/material";
import { ReactComponent as Google } from "../static/Icons/google.svg";
import ButtonCommon from "../CommonComponents/ButtonCommon";
import CommonModal from "../CommonComponents/CommonModal";
import { RightSideContainer } from "./DashboardLayout";
import { disconnectAccount, getConnections } from "../api/account";
import { showErrorToast, showSuccessToast } from "../toastUtils";
import dayjs from "dayjs";

export default function Account() {
  const { divHeight } = useContext(RightSideContainer);
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const res = await getConnections();
      if (res.success) {
        setLoading(false);
        setAccounts(res.data.connections);
      } else {
        setLoading(false);
        showErrorToast(res.message);
      }
    } catch (err) {
      setLoading(false);
      showErrorToast(err);
    }
  };

  // Helper function to format date robustly
  const formatAccountDate = (item) => {
    console.log("Date debug - item:", item);
    console.log("Date debug - createdAt:", item.createdAt, typeof item.createdAt);
    
    // Try different possible date fields and formats
    let dateValue = item.createdAt || item.created_at || item.timestamp || item.connectedAt;
    
    if (!dateValue) {
      return 'N/A';
    }
    
    try {
      // If it's already a number (Unix timestamp)
      if (typeof dateValue === 'number') {
        return dayjs.unix(dateValue).format("DD MMM YYYY");
      }
      
      // If it's a string, try different parsing methods
      if (typeof dateValue === 'string') {
        // First try parsing as Unix timestamp string (most common case)
        const unixTime = parseInt(dateValue, 10);
        if (!isNaN(unixTime) && unixTime > 0) {
          // Check if it's a reasonable Unix timestamp (after year 2000)
          if (unixTime > 946684800) { // Jan 1, 2000
            return dayjs.unix(unixTime).format("DD MMM YYYY");
          }
        }
        
        // Try parsing as ISO string
        let parsedDate = dayjs(dateValue);
        if (parsedDate.isValid()) {
          return parsedDate.format("DD MMM YYYY");
        }
      }
      
      return 'N/A';
    } catch (error) {
      console.error("Date parsing error:", error);
      return 'N/A';
    }
  };

  const renderToolIcon = (tool, size = 42) => {
    console.log("Account data:", tool);
    if (tool && tool.provider) {
      try {
        return (
          <img
            src={require(`../static/Icons/${tool.provider}.svg`)}
            alt={tool.toolName}
            style={{
              width: size,
              height: size,
              objectFit: "contain",
              borderRadius: 8,
            }}
          />
        );
      } catch (e) {
        // fallback if icon not found
        try {
          return (
            <img
              src={require(`../static/Icons/default-tool.svg`)}
              alt="default-tool"
              style={{
                width: size,
                height: size,
                objectFit: "contain",
                borderRadius: 8,
              }}
            />
          );
        } catch (e2) {
          return <Google style={{ width: size, height: size }} />;
        }
      }
    }
    // fallback if toolIcon is missing
    try {
      return (
        <img
          src={require(`../static/Icons/default-tool.svg`)}
          alt="default-tool"
          style={{
            width: size,
            height: size,
            objectFit: "contain",
            borderRadius: 8,
          }}
        />
      );
    } catch (e) {
      return <Google style={{ width: size, height: size }} />;
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const handleDisconnectClick = (provider) => {
    setSelectedProvider(provider);
    setShowConfirmModal(true);
  };

  const handleConfirmDisconnect = async () => {
    setLoading(true);
    setShowConfirmModal(false);
    try {
      const res = await disconnectAccount(selectedProvider);
      if (res.success) {
        showSuccessToast(res.message);
        await fetchAccounts();
      }
    } catch (err) {
      setLoading(false);
      showErrorToast(err);
    } finally {
      setLoading(false);
      setSelectedProvider(null);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ color: "#fff", mb: 2 }}>
        Connected accounts
      </Typography>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "200px",
            width: "100%",
          }}
        >
          <CircularProgress />
        </Box>
      ) : accounts.length > 0 ? (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            justifyContent: { xs: "center", sm: "flex-start" },
            maxHeight: `calc(${divHeight - 177}px)`,
            overflowY: "auto",
          }}
        >
          {accounts.map((item, index) => (
            <Box
              key={index}
              sx={{
                flex: "1 1 270px",
                maxWidth: "350px",
                background: "#D4E6FF17",
                borderRadius: "6px",
                display: "flex",
                flexDirection: "column",
                border: "1px solid #636363",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  p: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 42,
                      height: 42,
                      bgcolor: "#C7D3EA1F",
                    }}
                  >
                    {/* <Google /> */}
                    {renderToolIcon(item, 30)}
                  </Box>
                  <Typography
                    sx={{
                      color: "#D7D7D7",
                      fontWeight: "bold",
                      fontSize: 16,
                      textTransform: "capitalize",
                    }}
                  >
                    {item.provider}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "#C3C3C3",
                    textTransform: "capitalize",
                  }}
                >
                  Account: {item.provider}
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography sx={{ fontSize: 12, color: "#C3C3C3", display: "flex", alignItems: "center" }}>
                    {item.is_connected && (
                      <Chip
                        label="Connected"
                        size="small"
                        sx={{
                          bgcolor: "#FFFFFF59",
                          color: "#FFFFFF",
                          fontSize: 10,
                          fontWeight: "bold",
                          mr: 1,
                        }}
                      />
                    )}
                    <span>{formatAccountDate(item)}</span>
                  </Typography>
                  <ButtonCommon
                    variant="contained"
                    sx={{
                      width: "fit-content",
                      height: "fit-content",
                      fontSize: "12px",
                      borderRadius: 1,
                      p: 0,
                      px: 1,
                    }}
                    onClick={() => handleDisconnectClick(item.provider)}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={20} /> : "Disconnect"}
                  </ButtonCommon>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "600px",
            width: "100%",
            textAlign: "center",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: "#C3C3C3",
              mb: 1,
              fontSize: "18px",
              fontWeight: "500",
            }}
          >
            No Connected Accounts Found
          </Typography>
          {/* <Typography
            variant="body2"
            sx={{
              color: "#888",
              fontSize: "14px",
            }}
          >
            Connect your accounts to get started
          </Typography> */}
        </Box>
      )}

      {/* Confirmation Modal for Disconnect */}
      <CommonModal
        open={showConfirmModal}
        handleClose={() => {
          setShowConfirmModal(false);
          setSelectedProvider(null);
        }}
        title="Disconnect Account"
        cancelText="Cancel"
        submitText="Disconnect"
        submitProps={{
          onClick: handleConfirmDisconnect,
          disabled: loading,
          sx: { 
            bgcolor: "#A3E635", 
            color: "#000000",
            "&:hover": { bgcolor: "#B4F263" } 
          }
        }}
        cancelProps={{
          onClick: () => {
            setShowConfirmModal(false);
            setSelectedProvider(null);
          }
        }}
      >
        <Typography sx={{ color: "#C3C3C3", mb: 2 }}>
          Are you sure you want to disconnect your <strong>{selectedProvider}</strong> account?
        </Typography>
        <Typography sx={{ color: "#ff9999", fontSize: "14px" }}>
          This will remove access to all {selectedProvider} tools and you'll need to reconnect to use them again.
        </Typography>
      </CommonModal>
    </Box>
  );
}
